import type { JobAttachment } from '@payaca/types/jobTypes';
import { extensionsForCategories } from '@payaca/uploads';
import type { TIconName } from '@payaca/untitled-icons';
import { values } from 'lodash-es';

export const ImageFileExtensions = extensionsForCategories(['image']);
export const VideoFileExtensions = extensionsForCategories(['video']);
export const DocumentFileExtensions = extensionsForCategories(['document']);

const FileUploadType = {
  JOB_ATTACHMENT: 'jobAttachment',
  BUSINESS_TERMS: 'businessTerms',
  BUSINESS_ATTACHMENTS: 'businessAttachments',
  ACCOUNT_LOGO: 'accountLogo',
  LINE_ITEM_ATTACHMENT: 'lineItemAttachements',
  DEAL_DOCUMENT_FILE: 'dealDocumentFile',
};

const bytesInKb = 1024;

const FileSizeLimit = {
  [FileUploadType.JOB_ATTACHMENT]: 20 * bytesInKb * bytesInKb, // 20mb
  [FileUploadType.BUSINESS_TERMS]: 20 * bytesInKb * bytesInKb, // 20mb
  [FileUploadType.BUSINESS_ATTACHMENTS]: 20 * bytesInKb * bytesInKb, // 20mb
  [FileUploadType.ACCOUNT_LOGO]: 20 * bytesInKb * bytesInKb, // 20mb
  [FileUploadType.LINE_ITEM_ATTACHMENT]: 20 * bytesInKb * bytesInKb, // 20mb
  [FileUploadType.DEAL_DOCUMENT_FILE]: 20 * bytesInKb * bytesInKb, // 20mb
};
// Postmark email has total email size limit of 10mb, temporarily reduced file size to 7mb to allow for file size increase on base64 encoding which is used to attach to postmark email.

const getMbFileLimit = (fileLimit: number): string => {
  return `${fileLimit / bytesInKb / bytesInKb}MB`;
};

const fileSizeUnits = ['KB', 'MB', 'GB'] as const;

/**
 * A file's size in the largest unit that keeps it above 1, e.g. `1.4 MB`.
 * Sizes below a kilobyte are reported in whole bytes, since a fractional
 * `0.1 KB` reads as less precise than the `84 B` it stands for.
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < bytesInKb) {
    return `${bytes} B`;
  }

  let value = bytes / bytesInKb;
  let unitIndex = 0;

  while (value >= bytesInKb && unitIndex < fileSizeUnits.length - 1) {
    value /= bytesInKb;
    unitIndex++;
  }

  return `${value.toFixed(1)} ${fileSizeUnits[unitIndex]}`;
};

const ImageFileType = {
  JPEG: {
    name: ['image/jpeg', '.jpeg'],
    icon: 'image-01.3' as const,
  },
  PNG: {
    name: ['image/png', '.png'],
    icon: 'image-01.3' as const,
  },
};

const FileType = {
  ...ImageFileType,
  PDF: {
    name: ['application/pdf', '.pdf'],
    icon: 'pdf' as const,
  },
  DOC: {
    name: ['application/msword', '.doc'],
    icon: 'file-word' as const,
  },
};

const getFileType = (url: string) => {
  const filename = url && url.split('/').pop();
  // Infer the type of the image
  if (filename) {
    const match = /\.(\w+)$/.exec(filename);
    return match && match[0];
  }
  return null;
};

// oxlint-disable-next-line typescript/no-redundant-type-constituents -- FIXME: pre-existing error: 'any' overrides all other types in this union type.
const getSpecificFileTypeIcon = (file: any | JobAttachment) => {
  let type: string | null = null;

  if (file.file) {
    // locally uploaded file (i.e not uploaded to BE yet)
    type = file.file.type;
  } else {
    // uploaded file with a url
    const url = file.attachmentUrl || file.termUrl;
    if (file.type) {
      type = file.type;
    } else if (url) {
      type = getFileType(url);
    }
  }
  return values(FileType).find((v: { name: string[]; icon: any }) =>
    v.name.find((n) => n === type)
  );
};

// Get file type icon, defaults to generic file icon if type not found
// oxlint-disable-next-line typescript/no-redundant-type-constituents -- FIXME: pre-existing error: 'any' overrides all other types in this union type.
const getFileTypeIcon = (file: any | JobAttachment): TIconName => {
  const found = getSpecificFileTypeIcon(file);
  return found ? found.icon : 'file-04';
};

// Get file type icon, defaults to download file icon if type not found
const getFileTypeIconDownload = (file: JobAttachment): TIconName => {
  const found = getSpecificFileTypeIcon(file);
  return found ? found.icon : 'file-download-03.3';
};

const FileTypeExtensionsMap = {
  image: ImageFileExtensions,
  video: VideoFileExtensions,
  document: DocumentFileExtensions,
  /** @deprecated Spreadsheets and text are documents. Pass `document`. */
  other: DocumentFileExtensions,
};

/**
 * Extensions to offer in a file picker, for the `accept` attribute. A hint to
 * the operating system only — the server decides what is actually allowed.
 */
export const getAcceptedFileTypes = (
  types: ('video' | 'document' | 'image' | 'other')[]
) => [...new Set(types.flatMap((type) => FileTypeExtensionsMap[type] ?? []))];

export {
  FileSizeLimit,
  FileType,
  FileUploadType,
  formatFileSize,
  getFileType,
  getFileTypeIcon,
  getFileTypeIconDownload,
  getMbFileLimit,
  ImageFileType,
};
